import { injectable } from 'inversify';

import { t } from '@vk-hr-tek/core/translations';
import { ChipColorUnion } from '@vk-hr-tek/ui/ColoredChip';
import { FormatService } from '@vk-hr-tek/core/format';

import {
  VacationExcludedEmployee,
  VacationEntityApprover,
  VacationSchedule,
  VacationGroupApprover,
  VacationManagerApprover,
  VacationAssistantApprover,
  VacationValidators,
} from '@app/gen/vacations';

type StatusType = 'new' | 'active' | 'completed' | 'canceled' | 'starting';

@injectable()
export class VacationDetailMapper {
  constructor(private format: FormatService) {}

  private processScheduleStatus(status: StatusType): {
    statusName: string;
    statusChipColor: ChipColorUnion;
  } {
    switch (status) {
      case 'active':
        return { statusName: 'Активно', statusChipColor: 'green' };

      case 'completed':
        return {
          statusName: 'Завершено',
          statusChipColor: 'gray',
        };

      case 'new':
        return {
          statusName: 'Создано',
          statusChipColor: 'blue',
        };

      case 'canceled':
        return { statusName: 'Отклонён', statusChipColor: 'red' };

      case 'starting':
        return {
          statusName: t(
            'settings.mappers.vacationDetail.tasksCreationForEmployeeText',
          ),
          statusChipColor: 'gray',
        };
    }
  }

  private processGroupApprovers(approvers: VacationEntityApprover[]) {
    return approvers.map((approver) => ({
      groupName: approver.group_approve,
      deadline: approver.deadline,
      canEdit: approver.can_edit.toString(),
    }));
  }

  private processExcludedEmployees(employees: VacationExcludedEmployee[]) {
    return employees.map((employee) => ({
      label: `${employee.personnel_number}, ${employee.last_name} ${
        employee.first_name[0]
      }. ${employee.middle_name ? `${employee.middle_name[0]}.` : ''}`,
      value: employee.personnel_number,
      error: !!employee.dismissed_at,
      fio: `${employee.last_name} ${employee.first_name}${
        employee.middle_name ? ` ${employee.middle_name}` : ''
      }`,
      personalNumber: employee.personnel_number,
      position: employee.position,
      dismissedAt:
        employee.dismissed_at && this.format.toDate(employee.dismissed_at),
    }));
  }

  private processApprovalTypeValue(
    groupApprove?: VacationGroupApprover,
    assistantApprove?: VacationAssistantApprover,
    managerApprove?: VacationManagerApprover,
  ) {
    if (groupApprove) {
      return 'groups';
    } else if (assistantApprove) {
      return 'assistant';
    } else if (managerApprove) {
      return 'manager';
    } else {
      return '';
    }
  }

  private processApprovers(approvers: VacationEntityApprover[]) {
    return approvers.map(
      ({
        can_edit,
        deadline,
        group_approve,
        assistant_approve,
        manager_approve,
      }) => ({
        deadline,
        canEdit: can_edit.toString(),
        approvalTypeValue: this.processApprovalTypeValue(
          group_approve,
          assistant_approve,
          manager_approve,
        ),
        groupApprove: {
          isNotEmpty: !!group_approve,
          groupName: group_approve?.group_name,
        },
        managerApprove: {
          isNotEmpty: !!manager_approve,
          value: (!!manager_approve).toString(),
          approveLevels: manager_approve?.approve_levels,
          type: manager_approve?.type,
        },
        assistantApprove: {
          isNotEmpty: !!assistant_approve,
          value: (!!assistant_approve).toString(),
          approveLevels: assistant_approve?.approve_levels,
          type: assistant_approve?.type,
        },
      }),
    );
  }

  processValidatorsGroups(schedule: VacationSchedule) {
    if (
      !schedule.validator_vacation_types ||
      !Object.keys(schedule.validator_vacation_types).length
    ) {
      return [];
    }

    return Object.keys(schedule.validator_vacation_types).map(
      (validatorType) => {
        return {
          type: validatorType,
          validators: this.processValidators(
            (
              schedule.validator_vacation_types as Record<
                string,
                VacationValidators
              >
            )[validatorType] as VacationSchedule['validators'],
          ),
        };
      },
    );
  }

  processValidators(validators: VacationSchedule['validators']) {
    return {
      daysMustBeDivisibleBy: validators.days_must_be_divisible_by?.toString(),
      limitToCalculatedDays: validators.limit_to_calculated_days,
      maxDuration: validators.max_duration
        ? {
            ...validators.max_duration,
            days: validators.max_duration?.days?.toString(),
          }
        : undefined,
      minDuration: validators.min_duration
        ? {
            ...validators.min_duration,
            days: validators.min_duration?.days?.toString(),
          }
        : undefined,
      mustScheduleAllDays: validators.must_schedule_all_days,
      mustSchedule14days: validators.must_schedule_14days,
      weekendExclusion: validators.weekend_exclusion,
      min5DaysBetweenNotWorkdays: validators.min_5_days_between_not_workdays,
      minSpecificWeekendDays: validators.min_specific_weekend_days,
      workingDay: validators.working_day,
    };
  }

  processVacationSchedule(schedule: VacationSchedule) {
    return {
      id: schedule.id,
      activeTo: this.format.toDate(schedule.active_to),
      activeToValue: schedule.active_to,
      addPreviousVacation: schedule.add_previous_vacation.toString(),
      allowEmployeeComment: schedule.allow_employee_comment.toString(),
      availableAddingEmployees: schedule.available_adding_employees,
      employeeCanSeeVacations: schedule.teammate_access ? 'true' : 'false',
      teammateAccess: schedule.teammate_access || '',
      companyComment: schedule.company_comment,
      createdAt: this.format.toDate(schedule.created_at),
      employeeDeadline: schedule.employee_deadline,
      eventStats: {
        canceled: schedule.event_stats.canceled,
        completed: schedule.event_stats.completed,
        onFilling: schedule.event_stats.new,
        onApprove: schedule.event_stats.on_approve,
        total: schedule.event_stats.total,
        sendTo1c: schedule.event_stats.send_to_1c,
      },
      approvers: this.processApprovers(schedule.approvers || []),
      startOptions: {
        excludeDisabledContractUntil: this.format.toDate(
          schedule.start_options.exclude_disabled_contract_until || '',
        ),
        excludeMaternityLeaveUntil: this.format.toDate(
          schedule.start_options.exclude_maternity_leave_until || '',
        ),
        excludeUnpayedVacationUntil: this.format.toDate(
          schedule.start_options.exclude_unpayed_vacation_until || '',
        ),
        excludeEmployees: this.processExcludedEmployees(
          schedule.start_options.exclude_employees,
        ),
      },
      status: schedule.status,
      validatorsGroups: this.processValidatorsGroups(schedule),
      validators: this.processValidators(schedule.validators),
      year: schedule.year,
      units: schedule.units,
      ...this.processScheduleStatus(schedule.status),
    };
  }
}
